import { API_PREFIX, requestHeaders } from './auth'

// R572 (b2): the till's drawer day, its cash outs and "Delivered, cash in". The routes are R572
// (a)'s (master-layer/src/drawer/drawer.controller.ts), with (a2)'s money bodies (Rook #69117:
// startUsd, countedUsd, handedUsd as two-decimal strings, never JSON numbers) and (b0)'s read
// GET /admin/till/cash-due. None of these goes through apiSend or the offline queue: a day's
// Start and End and a cash out's Paid are deliberate till writes that must say done or refused
// where they were made, so offline they refuse with a plain line and send nothing. The headers
// are the till's own (requestHeaders, read only); no session code changes here.

/** The open day as the server shows it (drawer.service.ts dayView). Money is a string. */
export interface DrawerDay {
  id: string
  status: string
  startUsd: string
  startLiraNote: string | null
  startedAt: string
  endedAt: string | null
  expectedUsd: string | null
  endCountedUsd: string | null
  differenceUsd: string | null
  endHandedUsd: string | null
  endLiraNote: string | null
}

/** A cash out paid in the open day, as (a2) lists it on GET /drawer-days/open (Rook #69156 1). */
export interface PaidCashOut {
  id: string
  payeeKind: string
  payeeText: string
  amountUsd: string
  paidAt: string
}

/**
 * GET /drawer-days/open: the day, `empty`, and (a2)'s `paidCashOuts` (the day's paid cash outs);
 * with a typed count also counted, expected, difference.
 */
export interface DrawerPreview extends DrawerDay {
  empty: boolean
  countedUsd?: string
  paidCashOuts?: PaidCashOut[]
}

/** A cash out request (cash-outs.service.ts view()). */
export interface CashOutRequest {
  id: string
  payeeKind: string
  payeeText: string
  amountUsd: string
  reason: string
  raisedBy: string
  raisedByName: string | null
  raisedAt: string
  status: string
  paidAt: string | null
}

/** One row of (b0)'s GET /admin/till/cash-due, with the placedAt (b0) carries. */
export interface CashDueRow {
  id: string
  code: string
  customerName: string | null
  totalAmount: string
  driverName: string | null
  hasCashRecord: boolean
  placedAt: string
}

export interface CashDue {
  since: string
  outForDelivery: CashDueRow[]
  deliveredNoRecord: CashDueRow[]
}

/** POST /orders/:id/delivered-cash-in's answer (delivered-cash-in.service.ts). */
export interface CashInAnswer {
  orderId: string
  code: string
  case: 'stop_closed' | 'record_only' | 'confirm_only'
  cashRecordId: string
  amountUsd: string | null
}

export type DayResult<T> =
  | { ok: true; value: T }
  | { ok: false; kind: 'offline' }
  | { ok: false; kind: 'network' }
  | { ok: false; kind: 'refused'; status: number; message: string }

/** Dollars with at most two decimals ("50", "50.5", "50.50"), the one rule with HOP's cash-out form. */
export const USD_TYPED = /^\d{1,10}(\.\d{1,2})?$/

/**
 * The typed amount as it is sent, padded to two decimals ("50" is "50.00", "50.5" is "50.50";
 * Rook #69156 3, #69141), or null when the form must refuse it (a third decimal, a letter).
 */
export function dollarsText(typed: string, allowZero = true): string | null {
  const text = typed.trim()
  if (!USD_TYPED.test(text)) return null
  const [whole, frac = ''] = text.split('.')
  const padded = `${whole}.${frac.padEnd(2, '0')}`
  if (!allowZero && /^0+\.00$/.test(padded)) return null
  return padded
}

/** Cents of a two-decimal string, as an integer (no float on money). */
export function centsOf(text: string): number {
  const [whole, frac = '0'] = text.split('.')
  return Number(whole) * 100 + Number(frac.padEnd(2, '0').slice(0, 2))
}

/** The server's own words: master-layer's envelope nests them under `error.message`. */
async function serverWords(res: Response): Promise<string> {
  try {
    const data = (await res.json()) as { error?: { message?: unknown } | string; message?: unknown } | null
    const nested = data && typeof data.error === 'object' ? data.error.message : undefined
    if (typeof nested === 'string') return nested
    if (Array.isArray(nested)) return nested.join(' ')
    if (typeof data?.error === 'string') return data.error
    if (typeof data?.message === 'string') return data.message
  } catch {
    // no readable body
  }
  return `Request refused (${res.status})`
}

async function call<T>(method: 'GET' | 'POST', path: string, body?: Record<string, string>): Promise<DayResult<T>> {
  if (method === 'POST' && !navigator.onLine) return { ok: false, kind: 'offline' }
  let res: Response
  try {
    res = await fetch(`${API_PREFIX}${path}`, {
      method,
      headers: requestHeaders(method, method === 'POST'),
      ...(method === 'POST' ? { body: JSON.stringify(body ?? {}) } : {}),
    })
  } catch {
    return { ok: false, kind: navigator.onLine ? 'network' : 'offline' }
  }
  if (res.ok) return { ok: true, value: (await res.json()) as T }
  return { ok: false, kind: 'refused', status: res.status, message: await serverWords(res) }
}

export function getOpenDay(): Promise<DayResult<DrawerPreview>> {
  return call('GET', '/drawer-days/open')
}

export function previewEnd(countedUsd: string): Promise<DayResult<DrawerPreview>> {
  return call('GET', `/drawer-days/open?countedUsd=${encodeURIComponent(countedUsd)}`)
}

export function startDay(startUsd: string, startLiraNote: string): Promise<DayResult<DrawerDay>> {
  const note = startLiraNote.trim()
  return call('POST', '/drawer-days/start', note ? { startUsd, startLiraNote: note } : { startUsd })
}

export interface EndBody {
  countedUsd?: string
  handedUsd?: string
  endLiraNote?: string
}

export function endDay(body: EndBody): Promise<DayResult<DrawerDay>> {
  const out: Record<string, string> = {}
  if (body.countedUsd !== undefined) out.countedUsd = body.countedUsd
  if (body.handedUsd !== undefined) out.handedUsd = body.handedUsd
  if (body.endLiraNote && body.endLiraNote.trim()) out.endLiraNote = body.endLiraNote.trim()
  return call('POST', '/drawer-days/open/end', out)
}

export function getOpenCashOuts(): Promise<DayResult<CashOutRequest[]>> {
  return call('GET', '/cash-outs/open')
}

export function payCashOut(id: string): Promise<DayResult<CashOutRequest>> {
  return call('POST', `/cash-outs/${encodeURIComponent(id)}/paid`)
}

export function getCashDue(): Promise<DayResult<CashDue>> {
  return call('GET', '/admin/till/cash-due')
}

export function deliveredCashIn(orderId: string): Promise<DayResult<CashInAnswer>> {
  return call('POST', `/orders/${encodeURIComponent(orderId)}/delivered-cash-in`)
}
